import React, { FC } from "react";
import { Link } from "../link";
import Cookies from "js-cookie";
import { FormToggle } from "../form";
import type { TranslationItem } from "@/translations";

interface Props {
  onClose: () => void;
  cookiesPolicyUrl: TranslationItem;
}

export const CookiesConsent: FC<Props> = ({ onClose, cookiesPolicyUrl }) => {
  const [openCookiesSettings, setOpenCookiesSettings] = React.useState(false);
  const [analyticsAndPerformance, setAnalyticsAndPerformance] = React.useState(
    typeof Cookies.get("analytics-and-performance") === "boolean"
      ? Cookies.get("analytics-and-performance") === "true"
      : true
  );
  const [functionality, setFunctionality] = React.useState(
    typeof Cookies.get("functionality") === "boolean"
      ? Cookies.get("functionality") === "true"
      : true
  );
  const [targeting, setTargeting] = React.useState(
    typeof Cookies.get("targeting") === "boolean"
      ? Cookies.get("targeting") === "true"
      : true
  );

  const acceptAllCookies = () => {
    Cookies.set("necessary", "true", { expires: 180 });
    Cookies.set("analytics-and-performance", "true", { expires: 180 });
    Cookies.set("functionality", "true", { expires: 180 });
    Cookies.set("targeting", "true", { expires: 180 });
    onClose();
  };

  const acceptSelection = () => {
    Cookies.set("necessary", "true", { expires: 180 });
    Cookies.set("analytics-and-performance", String(analyticsAndPerformance), {
      expires: 180,
    });
    Cookies.set("functionality", String(functionality), { expires: 180 });
    Cookies.set("targeting", String(targeting), { expires: 180 });
    onClose();
  };

  return (
    <div className={`cookiesConsent ${openCookiesSettings ? "settings" : ""}`}>
      {!openCookiesSettings ? (
        <>
          <div className="cookiesConsent--text">
            <p>
              By clicking “Accept All Cookies” you agree to store cookies on
              your device to enhance site navigation, analyze site usage, and
              assist our marketing efforts. To find out more about the specific
              cookies we use, what they are used for, and how long they last,
              please visit our{" "}
              {cookiesPolicyUrl.link?.uri && (
                <Link href={cookiesPolicyUrl.link.uri} noStyle>
                  Cookie policy
                </Link>
              )}
            </p>
          </div>
          <div className="cookiesConsent--actions">
            <button
              className="link link_blue link_full"
              data-title="Accept All Cookies"
              onClick={acceptAllCookies}
            >
              <span>Accept All Cookies</span>
            </button>
            <button
              className="link link_blue link_outline"
              data-title="Cookies Settings"
              onClick={() => setOpenCookiesSettings(true)}
            >
              <span>Cookies Settings</span>
            </button>
          </div>
        </>
      ) : (
        <div className="cookiesConsent--settings">
          <div className="cookiesConsent--settings-title">
            About Your Privacy
          </div>
          <p className="cookiesConsent--settings-text">
            As explained in our Privacy Policy, like many companies, we use
            cookies and other tracking technologies on our Site (referred to
            together from this point forward as “cookies”, unless otherwise
            stated).
          </p>
          <div className="cookiesConsent--settings-cookies">
            <div className="cookiesConsent--settings-cookie">
              <div className="cookiesConsent--settings-cookie-text">
                <div className="cookiesConsent--settings-cookie-title">
                  Strictly Necessary Cookies
                </div>
                <div className="cookiesConsent--settings-cookie-description">
                  Always Active
                </div>
              </div>
              <div className="cookiesConsent--settings-cookie-actions">
                {cookiesPolicyUrl.link?.uri && (
                  <Link
                    href={cookiesPolicyUrl.link.uri}
                    noStyle
                    className="cookiesConsent--settings-cookie-info"
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.5">
                        <path
                          d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 15V11"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 8H11.01"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                  </Link>
                )}
              </div>
            </div>
            <div className="cookiesConsent--settings-cookie">
              <div className="cookiesConsent--settings-cookie-text">
                <div className="cookiesConsent--settings-cookie-title">
                  Analytics-and Performance-Related Cookies
                </div>
              </div>
              <div className="cookiesConsent--settings-cookie-actions">
                <FormToggle
                  value={analyticsAndPerformance}
                  onChange={() => {
                    setAnalyticsAndPerformance(!analyticsAndPerformance);
                  }}
                />
                {cookiesPolicyUrl.link?.uri && (
                  <Link
                    href={cookiesPolicyUrl.link.uri}
                    noStyle
                    className="cookiesConsent--settings-cookie-info"
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.5">
                        <path
                          d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 15V11"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 8H11.01"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                  </Link>
                )}
              </div>
            </div>
            <div className="cookiesConsent--settings-cookie">
              <div className="cookiesConsent--settings-cookie-text">
                <div className="cookiesConsent--settings-cookie-title">
                  Functionality-Related Cookies
                </div>
              </div>
              <div className="cookiesConsent--settings-cookie-actions">
                <FormToggle
                  value={functionality}
                  onChange={() => {
                    setFunctionality(!functionality);
                  }}
                />
                {cookiesPolicyUrl.link?.uri && (
                  <Link
                    href={cookiesPolicyUrl.link.uri}
                    noStyle
                    className="cookiesConsent--settings-cookie-info"
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.5">
                        <path
                          d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 15V11"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 8H11.01"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                  </Link>
                )}
              </div>
            </div>
            <div className="cookiesConsent--settings-cookie">
              <div className="cookiesConsent--settings-cookie-text">
                <div className="cookiesConsent--settings-cookie-title">
                  Targeting-Related Cookies
                </div>
              </div>
              <div className="cookiesConsent--settings-cookie-actions">
                <FormToggle
                  value={targeting}
                  onChange={() => {
                    setTargeting(!targeting);
                  }}
                />
                {cookiesPolicyUrl.link?.uri && (
                  <Link
                    href={cookiesPolicyUrl.link.uri}
                    noStyle
                    className="cookiesConsent--settings-cookie-info"
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.5">
                        <path
                          d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 15V11"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 8H11.01"
                          stroke="#020611"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                  </Link>
                )}
              </div>
            </div>
          </div>
          <div className="cookiesConsent--settings-actions">
            <button
              className="link link_blue link_outline"
              data-title="Accept Selection"
              onClick={acceptSelection}
            >
              <span>Accept Selection</span>
            </button>
            <button
              className="link link_blue link_full"
              data-title="Accept All Cookies"
              onClick={acceptAllCookies}
            >
              <span>Accept All Cookies</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
